import React from 'react';
import Promise from 'bluebird';
import _ from 'lodash';
import { getContext } from 'recompose';

function noop() {}

// to learn how to decorate functions vs class methods, see [https://github.com/wycats/javascript-decorators]
// in short, when decorating a normal function we simply get it as the first parameter, and we return the new function
// when decorating class methods we get the class prototype, the method name and a descriptor object which contains the actual method definition
export function debounce(delay = 250, options = {}) {
  return function debounceDecorator(target, name, descriptor) {
    // if name is undefined target is a normal function, so we can return the debounced function right away
    if (typeof name === 'undefined') {
      return _.debounce(target, delay, options);
    }

    // if name is not undefined, we're working on a class method and need to make sure to call the debounced method with the correct context (this)
    const debouncedMethod = _.debounce(descriptor.value, delay, options);
    descriptor.value = function debounceDecoratorDescriptorFunction() {
      return debouncedMethod.apply(this, arguments);
    };
  };
}

export function throttle(delay = 250, options = {}) {
  return function throttleDecorator(target, name, descriptor) {
    if (typeof name === 'undefined') {
      return _.throttle(target, delay, options);
    }

    const throttledMethod = _.throttle(descriptor.value, delay, options);
    descriptor.value = function throttleDecoratorDescriptorFunction() {
      return throttledMethod.apply(this, arguments);
    };
  };
}

// cache the promise returned from the method, but also make sure that the cache will not
// be cleared until the promise has resolved or rejected, no matter how short the cacheTime is
// (this makes sure that the cached function is never called until the previous operation has finished)
// handles non-promises as well, but will still expire the cache async
export function cachePromise(cacheTime) {
  const cache = {};

  return function cachePromiseDecorator(target, name, descriptor) {
    const method = descriptor.value;

    descriptor.value = function cachePromiseDecoratorDescriptorFunction(...args) {
      const key = JSON.stringify(args);

      if (cache[key]) return cache[key];

      // when the cache has expired, **wait for the promise to resolve or fail** and then remove the cacheTime
      // (this is to make sure several identical requests result in only one network request, even for low cache times)
      setTimeout(() => {
        // delete the cache after resolving or rejecting the promise, ignoring any errors
        Promise.resolve(cache[key]).finally(() => delete cache[key]).catch(noop);
      }, cacheTime);

      cache[key] = method.apply(this, args);

      return cache[key];
    };
  };
}

export function loadAction(action, secondaryAction) {
  return function loadActionDecorator(target) {
    target.loadActions = _.isArray(target.loadActions) ? target.loadActions : [];
    target.loadActions.push({ action, secondaryAction });

    return target;
  };
}

export function appContext(target) {
  target.contextTypes = {
    app: React.PropTypes.shape({
      adIdGenerator: React.PropTypes.object.isRequired,
      t: React.PropTypes.func.isRequired,
      serverTime: React.PropTypes.func.isRequired,
    }).isRequired,
  };

  return target;
}

export const getAppContext = getContext({
  app: React.PropTypes.shape({
    adIdGenerator: React.PropTypes.object.isRequired,
    t: React.PropTypes.func.isRequired,
    serverTime: React.PropTypes.func.isRequired,
  }).isRequired,
});



// WEBPACK FOOTER //
// ./src/util/decorators.js